import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert } from '@mui/material';
import { CircularProgress, TextField } from '@cere/cere-design-system';
import type { NodeMouseHandler } from 'reactflow';
import { VaultRequestError, type MemoryRows, type MembershipSummary, type SignedRoster } from '@cef-ai/vault-sdk';
import { toDisplayError } from '@/lib/vault/step-up-error';
import { ceilingOfGrant, type MemberCeiling } from '@/lib/clients/vault/orgs';
import {
  HUB_NODE_PREFIX,
  MemoryGraphCanvas,
  MemoryPrivacyLegend,
  groupHubs,
  hubChipNode,
  layout,
  memoryEdge,
  memoryNode,
  type MemoryGraphNodeData,
} from './MemoryGraphCanvas';
import { MemoryDetailPane, type MemoryRecordDetail } from './MemoryDetailPane';
import { MemoryEmptyState, type MemoryAccess } from './MemoryEmptyState';
import {
  DEFAULT_EXPANSION_BUDGET,
  MemoryExpandDialog,
  type AdvancedExpansion,
  type ExpansionDirection,
} from './MemoryExpandDialog';
import { MemoryCommandPalette, type MemoryPaletteRecord } from './MemoryCommandPalette';
import type { MemoryCiter } from './MemoryEvidenceStrip';
import type { MemoryQuerySnapshot } from './savedViews';
import { recencyOpacityById } from './recency';
import '@/src/styles/orgs.css';
import '@/src/styles/orgs-memory-interactions.css';

/**
 * The org's MEMORY BANK — the memory route's UI. Task 4's browse half: what
 * the org's runs and documents have taught it, newest first. Task 5's
 * explore half: click a row (or a node) and the record opens in a detail
 * pane while the graph grows around it, one neighbourhood read at a time.
 * Task 6's tier-1 query half: ONE shared query object — terms + type,
 * privacy and scope facets — drives the feed, the search, the type counts
 * and the canvas highlighting simultaneously (Obsidian-style search↔graph
 * sync). A non-empty (trimmed) terms box is `record_search`; empty terms
 * browse `record_list`; in both modes the type and scope facets compose
 * into the request body server-side, never a client-side filter over the
 * feed (a client filter would let the limit eat the results). Search hits
 * render like feed rows — snippet included, the FTS markers turned into
 * `<mark>` spans — and open the same pane + graph: a hit is just another
 * record. Both panels page through the response `cursor` with a Load-more
 * append.
 *
 * Read-only (the SDK exposes no memory writes to browser clients on
 * purpose), rendered inside the org detail for ANY org the wallet belongs
 * to, owner or member: the server's gate (grants + privacy ceiling) IS the
 * filter, so a member simply sees fewer rows and the UI does not
 * double-gate on role. Facets cannot widen that gate — the server ANDs them
 * with it — and the gate readout counts the viewer's whole admitted bank,
 * narrowed only by the scope facet (see the counts effect).
 *
 * Reads ride the Bearer client (`readSdk` — never signs), each fetch in its
 * own effect with stale-response cancellation, the same contract
 * `useOrgDetail`/`useMemberCeilings` run under. A refused read (403/404) is
 * absence, never an error state — a refused feed or search is "no rows"
 * (the honest line says a hidden record is indistinguishable from an absent
 * one), a refused neighbourhood is "the node gains nothing", a refused
 * detail keeps the projection fields already disclosed; every other fault
 * (401, 5xx, network) surfaces as an Alert beside the content.
 *
 * The search rows' `rank` is the RAW bm25 value the server ordered by —
 * rendered as given, never re-sorted and never rounded (three decimals
 * would collapse the ordering).
 */

/** One page's cap, browse feed and search results alike: the panel pages
 *  with the response cursor; the readout, not the row list, is the viewer's
 *  true total. */
const LIST_LIMIT = 200;

/** The hub-grouping threshold (tier 2, Bloom's node-limit pattern): past
 *  this many VISIBLE edges on one node, the surplus collapses behind a
 *  `+N more` chip instead of a starburst. 20 keeps a hub legible while the
 *  chip still names an honest surplus (LOADED-VISIBLE edges only). */
const HUB_DEGREE_THRESHOLD = 20;

/** Focus mode's dim (Kumu's pattern): non-neighbours of the current
 *  selection render at this opacity — dimmed, never hidden (hiding is the
 *  gate's job, not a view mode's). */
const FOCUS_DIM_OPACITY = 0.25;

/** The graph's soft draw limit. Past it the canvas stops expanding and SAYS
 *  so — a silently truncated graph would misreport the bank's shape. */
const DEFAULT_GRAPH_NODE_CAP = 300;

/** How long the terms box must fall quiet before its query costs a GET.
 *  There is no explicit submit and no mode control separate from the input —
 *  the keystroke IS the mode switch — so the debounce is the one throttle
 *  every TERMS change passes through. Facet toggles do not: they re-query
 *  the ALREADY-settled terms immediately. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The SDK surface these reads need, narrowed like `OrgsReadSdk` so unit
 * tests pass a plain object and the Bearer `VaultSDK` satisfies it
 * structurally. Memory is a per-vault handle (`vault.byId(...).memory`),
 * so resolving the org vault is part of every read. The scope-chip
 * vocabulary rides the SAME handle's gated `scopes.list` (the server
 * narrows it to the viewer's grants), and the empty state's access
 * explainer reads the viewer's own membership (`memberships.mine` — always
 * the caller's own, keyed server-side) plus the signed roster (`memberships.
 * signedRoster` — Bearer, never signs).
 */
export interface MemoryReadSdk {
  vault: {
    byId: (vaultId: string) => Promise<MemoryVaultHandle>;
  };
  memberships: {
    mine: () => Promise<MembershipSummary[]>;
    signedRoster: (vaultId: string) => Promise<SignedRoster>;
  };
}

/** One scope the viewer may see, off the gated `scopes.list` read. */
export interface MemoryScopeOption {
  /** The wire name — the only value a `scope` facet may carry. */
  name: string;
  /** `displayName ?? name` — what the chip renders. */
  label: string;
}

/** The per-vault memory read surface the section consumes (SDK `Memory`).
 *  The paged tools' opts carry `cursor` (SDK 4.5) and both paged tools plus
 *  `countByType` carry `scope`. */
export interface MemoryVaultHandle {
  scopes: {
    list: () => Promise<Array<{ name: string; displayName?: string }>>;
  };
  memory: {
    list: (type?: string, opts?: { limit?: number; scope?: string; cursor?: string }) => Promise<MemoryRows>;
    countByType: (opts?: { scope?: string }) => Promise<MemoryRows>;
    get: (id: string) => Promise<MemoryRows>;
    neighbours: (
      id: string,
      opts?: { limit?: number; edgeType?: string; direction?: 'in' | 'out' | 'both' },
    ) => Promise<MemoryRows>;
    search: (
      match: string,
      opts?: { limit?: number; type?: string; privacy?: string; scope?: string; cursor?: string },
    ) => Promise<MemoryRows>;
  };
}

/** One type chip: a record type and how many of it the viewer's gate admits. */
export interface MemoryTypeCount {
  type: string;
  count: number;
}

/** One feed row — the `record_list` projection, mapped off the wire columns. */
export interface MemoryRecordRow {
  id: string;
  type: string;
  title: string;
  scope: string;
  privacy: string;
  updatedAt: string;
}

/** One search hit — the `record_search` projection: the browse row's columns
 *  with `updated_at` replaced by the RAW bm25 rank (best first; more negative
 *  is better) and the FTS `snippet` appended (SDK 4.5's seventh column). The
 *  rank is kept exactly as the server sent it — rounding it to three places
 *  collapses the ordering, and re-sorting would replace the server's ordering
 *  with a guess. The snippet keeps its `⟦`/`⟧` markers verbatim; only the
 *  renderer splits on them. */
export interface MemorySearchRow {
  id: string;
  type: string;
  title: string;
  scope: string;
  privacy: string;
  rank: number | null;
  snippet: string | null;
}

/** What a row needs to be selected: the projection fields both panels share.
 *  A search hit is just another record — it seeds the graph and the pane's
 *  fallback exactly like a browse row. `updatedAt` rides along when the
 *  projection carried it (the feed's; the search rows replaced it with the
 *  rank) so the recency encoding can use it where it is known. */
interface RowSelection {
  id: string;
  type: string;
  title: string;
  scope: string;
  privacy: string;
  updatedAt: string | null;
}

/** A rendered row: the shared projection plus the one right-aligned meta line
 *  the panel's own projection carries — `updated_at` for browse, the raw rank
 *  for search — and, for search hits that HAVE one, the snippet node. One
 *  renderer draws both panels' rows. */
interface RowView extends RowSelection {
  meta: string;
  snippet: ReactNode | null;
}

/**
 * THE query — one object driving feed, search, counts and canvas
 * highlighting at once (tier 1's single source of truth). `terms` is the
 * raw match input; the three facets are the chips. A facet held as `null`
 * is an UNSET axis: request bodies omit the key entirely (the SDK's
 * `omitempty` rule — never an empty string), chips render unpressed, and
 * the counts ignore it.
 */
interface MemoryQuery {
  terms: string;
  type: string | null;
  privacy: string | null;
  scope: string | null;
}

const EMPTY_QUERY: MemoryQuery = { terms: '', type: null, privacy: null, scope: null };

/**
 * The panel's shape: `browse` is the chip feed; `search` renders
 * `record_search` results off the terms box, with the chips composed into
 * the query body as facets.
 */
type PanelMode = 'browse' | 'search';

/** The graph's whole mutable state in one object, so an expansion's
 *  nodes+edges+capped merge is atomic (two concurrent expansions cannot
 *  drop each other's far records). */
interface MemoryGraph {
  nodes: Map<string, MemoryGraphNodeData>;
  /** Keyed `${source}->${target}` — the same relation read twice draws once. */
  edges: Map<string, { source: string; target: string; label: string | null }>;
  /** The params each completed-and-marked expansion ran with, keyed by node
   *  id. A REFUSED expansion stays marked: a 403/404 is a permissions
   *  boundary, and re-asking it gains nothing. The STORED params matter for
   *  the hub chips — a chip's next batch re-reads the SAME edge filter with
   *  a higher limit (neighbours orders by far id, so the bigger ask is a
   *  deterministic superset of the page already drawn). */
  expanded: Map<string, ExpansionRecord>;
  /** Per-hub REVEALED allowance (in edges) unlocked by that hub's `+N more`
   *  chip clicks — the grouping pass draws `threshold + revealed[hub]` edges
   *  before grouping that hub's surplus. Rides the graph so a vault switch
   *  resets it with everything else. */
  revealed: Map<string, number>;
  capped: boolean;
}

/** One marked expansion: the filter shape plus the limit actually sent. */
interface ExpansionRecord {
  edgeType: string | null;
  direction: ExpansionDirection;
  limit: number;
}

const EMPTY_GRAPH: MemoryGraph = {
  nodes: new Map(), edges: new Map(), expanded: new Map(), revealed: new Map(), capped: false,
};

/** One cell out of a positional columnar row, by column name → index. */
function cell(row: unknown[], columns: string[], name: string): unknown {
  const i = columns.indexOf(name);
  return i === -1 ? undefined : row[i];
}

function typeCountsOf({ columns, rows }: MemoryRows): MemoryTypeCount[] {
  return rows.map((row) => ({
    type: String(cell(row, columns, 'type') ?? ''),
    count: Number(cell(row, columns, 'count') ?? 0),
  }));
}

function recordsOf({ columns, rows }: MemoryRows): MemoryRecordRow[] {
  return rows.map((row) => ({
    id: String(cell(row, columns, 'id') ?? ''),
    type: String(cell(row, columns, 'type') ?? ''),
    title: String(cell(row, columns, 'title') ?? ''),
    scope: String(cell(row, columns, 'scope') ?? ''),
    privacy: String(cell(row, columns, 'privacy') ?? ''),
    updatedAt: String(cell(row, columns, 'updated_at') ?? ''),
  }));
}

/** `record_search`'s column names are all unique, so this maps by name like
 *  the feed does (the duplicate-`type` hazard is `record_neighbours`' alone).
 *  `rank` stays the raw number the server sent — never stringified through a
 *  formatter that could round it. `snippet` is read BY NAME like every other
 *  column, so an older server's six-column response simply maps it to null
 *  (the row degrades to title-only, silently). */
function searchRowsOf({ columns, rows }: MemoryRows): MemorySearchRow[] {
  return rows.map((row) => {
    const rank = cell(row, columns, 'rank');
    return {
      id: String(cell(row, columns, 'id') ?? ''),
      type: String(cell(row, columns, 'type') ?? ''),
      title: String(cell(row, columns, 'title') ?? ''),
      scope: String(cell(row, columns, 'scope') ?? ''),
      privacy: String(cell(row, columns, 'privacy') ?? ''),
      rank: rank == null ? null : Number(rank),
      snippet: str(cell(row, columns, 'snippet')),
    };
  });
}

/** The privacy axis's four values, least to most sensitive — the same order
 *  the graph legend ranks them. The server refuses an unknown privacy facet
 *  rather than answering it as empty, so the UI only ever offers these four. */
const PRIVACY_FACETS = ['public', 'internal', 'private', 'restricted'] as const;

const capitalise = (s: string): string => `${s.charAt(0).toUpperCase()}${s.slice(1)}`;

/**
 * The FTS snippet's `⟦…⟧` markers → highlighted `<mark>` spans, as a NODE —
 * or null when there is nothing to show, so the row renders title-only (the
 * older-server contract, degraded silently). Splitting on the markers and
 * letting React render the BETWEEN text is the whole escape story: snippet
 * text from the server can never inject markup. Unbalanced markers (a
 * server hiccup) degrade to highlighting the tail — never an error.
 */
function snippetNode(text: string | null): ReactNode {
  if (text === null || text === '') return null;
  const parts: ReactNode[] = [];
  let inMark = false;
  for (const part of text.split(/(⟦|⟧)/)) {
    if (part === '⟦') {
      inMark = true;
    } else if (part === '⟧') {
      inMark = false;
    } else if (part !== '') {
      parts.push(inMark ? <mark key={parts.length}>{part}</mark> : <span key={parts.length}>{part}</span>);
    }
  }
  return <>{parts}</>;
}

/** One browse row's right-aligned meta line: scope · Privacy · date. */
function browseRowView(r: MemoryRecordRow): RowView {
  return {
    id: r.id, type: r.type, title: r.title, scope: r.scope, privacy: r.privacy,
    updatedAt: r.updatedAt,
    meta: `${r.scope} · ${capitalise(r.privacy)} · ${r.updatedAt.slice(0, 10)}`,
    snippet: null,
  };
}

/** One search hit's meta line: scope · Privacy · the RAW rank. `String()` of
 *  the number the server sent — not a rounded rendering of it. The snippet
 *  node rides beside it (browse rows carry none; null when absent/empty).
 *  `updatedAt` is null BY PROJECTION — the search row's seventh-from-last
 *  column is the rank, so the recency encoding simply has nothing to read
 *  here (the record_get read supplies it once the hit is opened). */
function searchRowView(r: MemorySearchRow): RowView {
  return {
    id: r.id, type: r.type, title: r.title, scope: r.scope, privacy: r.privacy,
    updatedAt: null,
    meta: r.rank === null
      ? `${r.scope} · ${capitalise(r.privacy)}`
      : `${r.scope} · ${capitalise(r.privacy)} · rank ${String(r.rank)}`,
    snippet: snippetNode(r.snippet),
  };
}

const str = (v: unknown): string | null => (v == null ? null : String(v));

/** `record_get`'s full row. Its column names are all unique, so mapping by
 *  name (like the feed) is safe here — the duplicate-`type` hazard is
 *  `record_neighbours`' alone. Zero rows (record gone between list and
 *  click) maps to null: the pane falls back to what was disclosed. */
function detailOf({ columns, rows }: MemoryRows): MemoryRecordDetail | null {
  const row = rows[0];
  if (row === undefined) return null;
  return {
    id: String(cell(row, columns, 'id') ?? ''),
    type: str(cell(row, columns, 'type')),
    title: str(cell(row, columns, 'title')),
    body: str(cell(row, columns, 'body')),
    scope: str(cell(row, columns, 'scope')),
    privacy: str(cell(row, columns, 'privacy')),
    createdAt: str(cell(row, columns, 'created_at')),
    updatedAt: str(cell(row, columns, 'updated_at')),
  };
}

/**
 * `record_neighbours` maps BY POSITION, never by column name — the load-
 * bearing rule of the graph. The projection is `far.id, far.type, far.title,
 * far.scope, far.privacy, e.type`: positions 1 and 5 are BOTH named `type`
 * on the wire (the far record's and the EDGE's), and a name-keyed zip would
 * resolve `type` to the last column and silently repaint every far record
 * with its edge's type.
 */
function neighbourOf(row: unknown[]): { far: MemoryGraphNodeData; edgeType: string | null } {
  return {
    far: {
      id: row[0] == null ? '' : String(row[0]),
      type: str(row[1]),
      title: str(row[2]),
      scope: str(row[3]),
      privacy: str(row[4]),
    },
    edgeType: str(row[5]),
  };
}

/**
 * "The viewer may not see this" — a refused read, which renders as absence.
 * 403/404 only: 401, 5xx and network faults are real failures and surface
 * as Alerts.
 */
function isRefusal(e: unknown): boolean {
  return e instanceof VaultRequestError && (e.status === 403 || e.status === 404);
}

/** Append one page to another, deduped by id — a keyset page that repeats a
 *  row (server quirk, or a rapid double-fire that raced the disable) must
 *  not render it twice: the row key is the id. */
function appendPage<T extends { id: string }>(current: T[], next: T[]): T[] {
  const seen = new Set(current.map((r) => r.id));
  return [...current, ...next.filter((r) => !seen.has(r.id))];
}

/** The server's per-term quote strip (query.go's strings.Trim(term, "\"")),
 *  mirrored client-side so the MODE agrees with the wire before anything is
 *  sent: each whitespace-separated term loses its surrounding double quotes,
 *  and a box whose terms are ALL empty after the strip (blank, or quotes
 *  only — `""`) is a browse — the server would split it to zero terms and
 *  400, which is not a refusal and would surface as a transient Alert. */
function termsAreEmptyAfterQuoteStrip(terms: string): boolean {
  return terms.trim().split(/\s+/).every((term) => term.replace(/^"+|"+$/g, '') === '');
}

interface Props {
  /** The Bearer read client — same instance the org detail rides. */
  readSdk: MemoryReadSdk;
  /** The selected vault context: the org vault, or the owner's own vault. */
  vaultId: string;
  /** The graph's soft node cap (default 300); a prop so the cap SEMANTICS
   *  stay testable without building a 300-node fixture. */
  graphNodeCap?: number;
}

export function OrgMemoryBankSection({ readSdk, vaultId, graphNodeCap = DEFAULT_GRAPH_NODE_CAP }: Props) {
  // The terms box is the ONLY writer of `mode`: a query that is non-empty
  // AFTER the server's per-term quote strip is search mode, clearing it (or
  // leaving only quotes) returns the browse feed — the two can never disagree
  // because the same keystroke decides both. The QUERY is the one
  // shared state: terms + the type/privacy/scope facets, consumed by the
  // feed, the search, the counts and the canvas highlight together.
  const [query, setQuery] = useState<MemoryQuery>(EMPTY_QUERY);
  const mode: PanelMode = termsAreEmptyAfterQuoteStrip(query.terms) ? 'browse' : 'search';
  const [counts, setCounts] = useState<MemoryTypeCount[] | null>(null);
  const [countsLoading, setCountsLoading] = useState(true);
  const [countsFault, setCountsFault] = useState<string | null>(null);
  const [records, setRecords] = useState<MemoryRecordRow[] | null>(null);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [recordsFault, setRecordsFault] = useState<string | null>(null);
  const [searchRows, setSearchRows] = useState<MemorySearchRow[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFault, setSearchFault] = useState<string | null>(null);
  // The terms box's debounced twin: the RAW terms drive the input and the
  // mode flip (browse/search), while the query effect reads this — one
  // record_search per settled query, not one per character. Facet changes
  // re-query immediately (they ride the query object, not this twin).
  const [debouncedTerms, setDebouncedTerms] = useState('');

  // Cursor paging (tier 1): the response `cursor` while more pages exist,
  // null on the last page (an absent key — the server marks it omitempty).
  // One slot per panel; `moreLoading` guards the Load-more button, and the
  // ref is the synchronous double-click guard (state alone cannot stop two
  // clicks inside one React batch).
  const [browseCursor, setBrowseCursor] = useState<string | null>(null);
  const [searchCursor, setSearchCursor] = useState<string | null>(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreFault, setMoreFault] = useState<string | null>(null);
  const moreInFlightRef = useRef(false);
  // Bumped by every page-one query effect and the vault switch: a Load-more
  // response landing after the query moved on is stale and must not append.
  const pageEpochRef = useRef(0);

  // The scope-chip vocabulary, off the gated scopes.list (viewer-visible by
  // the server's own narrowing — the UI offers exactly what the read
  // returned, inventing nothing).
  const [scopeOptions, setScopeOptions] = useState<MemoryScopeOption[] | null>(null);
  const [scopesFault, setScopesFault] = useState<string | null>(null);

  // The empty state's access explainer: the viewer's own grants + ceiling.
  // A failed/unattributable read leaves it null — the explainer then states
  // the model without specifics, silently (an auxiliary read must not cost
  // the panel an Alert).
  const [access, setAccess] = useState<MemoryAccess | null>(null);

  // The explore half: one selected record, its full row, and the graph.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MemoryRecordDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailFault, setDetailFault] = useState<string | null>(null);
  const [graph, setGraph] = useState<MemoryGraph>(EMPTY_GRAPH);
  const [graphFault, setGraphFault] = useState<string | null>(null);

  // Tier 2 — expansion discipline: the standing node budget (the Advanced
  // Expansion dialog's last choice; plain ⌘E/double-click expansions and hub
  // chip batches ride it too), the dialog's target node, and focus mode.
  const [budget, setBudget] = useState(DEFAULT_EXPANSION_BUDGET);
  const [expandDialogFor, setExpandDialogFor] = useState<{ id: string; title: string | null } | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  // Tier 2 — the ⌘K palette.
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Synchronous guard for hub chip batches (two clicks on a chip inside one
  // React batch must not mint two reads — state alone cannot stop that).
  const hubBatchInFlightRef = useRef(new Set<string>());

  // A new vault is a new bank: every per-vault selection resets HERE, during
  // render — an effect-based reset would let the records effect below fetch
  // the new vault once under the old vault's type filter first (Task 5's
  // carried finding). Through OrgsSection the detail's loading gate happens
  // to remount this component anyway; resetting here makes the component's
  // own contract hold without depending on that gate.
  const [seenVaultId, setSeenVaultId] = useState(vaultId);
  if (seenVaultId !== vaultId) {
    setSeenVaultId(vaultId);
    setQuery(EMPTY_QUERY);
    setDebouncedTerms('');
    setSearchRows(null);
    setSearchFault(null);
    setSearchLoading(false);
    setSearchCursor(null);
    setBrowseCursor(null);
    setMoreLoading(false);
    setMoreFault(null);
    setScopeOptions(null);
    setScopesFault(null);
    setAccess(null);
    setSelectedId(null);
    setDetail(null);
    setDetailFault(null);
    setDetailLoading(false);
    setGraphFault(null);
    setGraph(EMPTY_GRAPH);
    // Tier 2 interaction state: the new bank starts unfocused, with no
    // palette or expansion dialog carried over from the old one. The BUDGET
    // survives — it is the viewer's standing client cap, not bank data.
    setFocusMode(false);
    setPaletteOpen(false);
    setExpandDialogFor(null);
    pageEpochRef.current += 1;
  }

  // The type chips' counts — `record_count_by_type`, GATED server-side.
  //
  // THE NARROWING RULE: counts reflect the current SCOPE facet only, never
  // the type or privacy facets and never the terms — countByType({scope})
  // narrows within the scope, and the chips must stay selectable: narrowing
  // by type would zero every unselected chip and lock the axis, and the
  // terms/terms+privacy slice would make the readout track the query instead
  // of the viewer's admitted bank.
  useEffect(() => {
    let cancelled = false;
    setCounts(null);
    setCountsFault(null);
    setCountsLoading(true);
    void (async () => {
      try {
        const vault = await readSdk.vault.byId(vaultId);
        const next = typeCountsOf(await vault.memory.countByType(
          query.scope !== null ? { scope: query.scope } : {},
        ));
        if (!cancelled) {
          setCounts(next);
          setCountsLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          if (isRefusal(e)) {
            setCounts([]);
          } else {
            setCountsFault(toDisplayError(e));
          }
          setCountsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readSdk, vaultId, query.scope]);

  // The scope-chip vocabulary: the gated scopes.list on the SAME byId handle
  // the memory reads resolve. A refusal (or a vault with no scopes) simply
  // offers no scope axis; only a non-refusal fault Alerts.
  useEffect(() => {
    let cancelled = false;
    setScopeOptions(null);
    setScopesFault(null);
    void (async () => {
      try {
        const vault = await readSdk.vault.byId(vaultId);
        const next = await vault.scopes.list();
        if (!cancelled) {
          setScopeOptions(next.map((s) => ({ name: s.name, label: s.displayName ?? s.name })));
        }
      } catch (e) {
        if (!cancelled && !isRefusal(e)) {
          setScopesFault(toDisplayError(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readSdk, vaultId]);

  // The empty state's access explainer. `memberships.mine()` is keyed to the
  // caller server-side, so it needs no identity of its own; no entry for this
  // vault means the vault is the wallet's OWN (ownership is key math — no
  // grant rows, no ceiling above). The CEILING is attributed only where the
  // server's own disclosure rule does the attribution: a PLAIN member is
  // served exactly their own grant(s) by the signed-roster route (§9.1), so
  // the served document IS the viewer's. An admin member is served the whole
  // roster, whose rows carry no viewer identity this surface can check —
  // their ceiling stays 'unknown' rather than being guessed (attribution by
  // grant-shape matching would be a heuristic, and a wrong ceiling in an
  // access explainer is exactly the lie this panel refuses to tell).
  // Tier 2 may pass the viewer pubkey down for exact attribution.
  useEffect(() => {
    let cancelled = false;
    setAccess(null);
    void (async () => {
      let accessNext: MemoryAccess | null = null;
      try {
        const mine = await readSdk.memberships.mine();
        const entry = mine.find((m) => m.vaultId === vaultId) ?? null;
        if (entry === null) {
          accessNext = { viewer: 'owner', grants: [], ceiling: 'unknown' };
        } else {
          const grants = Object.entries(entry.scopes ?? {}).map(([name, level]) => ({
            name,
            level: String(level),
          }));
          let ceiling: MemberCeiling = 'unknown';
          if (entry.role === 'member') {
            try {
              const roster = await readSdk.memberships.signedRoster(vaultId);
              if (roster.grants.length > 0) ceiling = ceilingOfGrant(roster.grants[0]!);
            } catch {
              // Not readable → unknown; absence of a read is not 'none'.
            }
          }
          accessNext = { viewer: 'member', grants, ceiling };
        }
      } catch {
        accessNext = null;
      }
      if (!cancelled) setAccess(accessNext);
    })();
    return () => {
      cancelled = true;
    };
  }, [readSdk, vaultId]);

  useEffect(() => {
    // Search mode owns the panel: the browse feed is not re-read while a
    // query is live (the facets narrow the QUERY, never a client-side slice
    // of the feed — a client filter would let the limit eat the results).
    if (mode !== 'browse') return;
    let cancelled = false;
    pageEpochRef.current += 1;
    setRecords(null);
    setRecordsFault(null);
    setMoreFault(null);
    setRecordsLoading(true);
    setBrowseCursor(null);
    void (async () => {
      try {
        const vault = await readSdk.vault.byId(vaultId);
        // Server order IS newest first; rendered as given, never re-sorted.
        // Unset facets are absent keys, never empty strings.
        const resp = await vault.memory.list(query.type ?? undefined, {
          limit: LIST_LIMIT,
          ...(query.scope !== null ? { scope: query.scope } : {}),
        });
        if (!cancelled) {
          setRecords(recordsOf(resp));
          setBrowseCursor(resp.cursor ?? null);
          setRecordsLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          if (isRefusal(e)) {
            setRecords([]);
          } else {
            setRecordsFault(toDisplayError(e));
          }
          setRecordsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readSdk, vaultId, mode, query.type, query.scope]);

  // The terms box's settle: every keystroke restarts the window, and the
  // cleanup cancels a pending one — a query superseded mid-flight costs
  // nothing. The twin lags the raw terms by design; the query effect below
  // treats "search mode with no settled query yet" as pending, never as a
  // zero-result answer.
  useEffect(() => {
    const settle = setTimeout(() => setDebouncedTerms(query.terms), SEARCH_DEBOUNCE_MS);
    return () => { clearTimeout(settle); };
  }, [query.terms]);

  // The query half: record_search with the facets COMPOSED into the body.
  // Unset facets are absent keys, not undefined ones — the wire body is
  // exactly the pre-facet shape whenever nothing is toggled on. The server
  // ANDs type/privacy/scope with the viewer's gate, so a facet can only
  // NARROW within what the viewer may already see; a refused read is zero
  // rows. Reads the DEBOUNCED terms (keystrokes settle first), while facet
  // and vault changes re-query immediately. Every run is a FRESH page one:
  // rows and cursor reset, so a Load-more from the superseded query cannot
  // append (the epoch guard below drops its response).
  useEffect(() => {
    if (mode !== 'search') return;
    const terms = debouncedTerms.trim();
    // Between the first keystroke and the settled query the twin is still
    // empty — and on the quotes-only→real transition it can still hold the
    // quotes-only box — an FTS term that is empty after the server's quote
    // strip is a garbage query, so wait it out.
    if (termsAreEmptyAfterQuoteStrip(debouncedTerms)) return;
    let cancelled = false;
    pageEpochRef.current += 1;
    setSearchRows(null);
    setSearchFault(null);
    setMoreFault(null);
    setSearchLoading(true);
    setSearchCursor(null);
    void (async () => {
      try {
        const vault = await readSdk.vault.byId(vaultId);
        const next = await vault.memory.search(terms, {
          limit: LIST_LIMIT,
          ...(query.type !== null ? { type: query.type } : {}),
          ...(query.privacy !== null ? { privacy: query.privacy } : {}),
          ...(query.scope !== null ? { scope: query.scope } : {}),
        });
        if (!cancelled) {
          setSearchRows(searchRowsOf(next));
          setSearchCursor(next.cursor ?? null);
          setSearchLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          if (isRefusal(e)) {
            setSearchRows([]);
          } else {
            setSearchFault(toDisplayError(e));
          }
          setSearchLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readSdk, vaultId, mode, debouncedTerms, query.type, query.privacy, query.scope]);

  // The selected record's full row (`record_get`). Refused → the pane keeps
  // the projection fields already disclosed (feed row / graph node): the
  // record exists and was partially disclosed; only its full row is hidden.
  useEffect(() => {
    if (selectedId === null) return;
    let cancelled = false;
    setDetail(null);
    setDetailFault(null);
    setDetailLoading(true);
    void (async () => {
      try {
        const vault = await readSdk.vault.byId(vaultId);
        const next = detailOf(await vault.memory.get(selectedId));
        if (!cancelled) {
          setDetail(next);
          setDetailLoading(false);
          // The recency encoding's second source: `record_get` is the one
          // read that carries `updated_at` for a node the neighbours
          // projection brought in without one. Merged into the graph node
          // (never a separate store) so the canvas and the pane's chip read
          // ONE value.
          if (next !== null && next.updatedAt !== null) {
            setGraph((g) => {
              const node = g.nodes.get(selectedId);
              // `!= null` covers BOTH absence spellings a node can carry:
              // `undefined` (a neighbours-sourced far node — `neighbourOf`
              // sets no key) and `null` (a search-hit seed, whose projection
              // replaced the column with the rank). A strict `!== null`
              // would skip the far nodes — exactly the nodes this merge
              // exists for. A row-seeded node already HOLDS its timestamp
              // and is left alone.
              if (node === undefined || node.updatedAt != null) return g;
              const nodes = new Map(g.nodes);
              nodes.set(selectedId, { ...node, updatedAt: next.updatedAt });
              return { ...g, nodes };
            });
          }
        }
      } catch (e) {
        if (!cancelled) {
          if (!isRefusal(e)) setDetailFault(toDisplayError(e));
          setDetailLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readSdk, vaultId, selectedId]);

  // The readout's N: the SUM of the chips' counts — the viewer's whole
  // admitted bank, narrowed by the SCOPE facet alone (that is the axis the
  // counts read), never by type/privacy/terms — a narrowed query must not
  // shrink it.
  const gateTotal = (counts ?? []).reduce((total, c) => total + c.count, 0);

  /** The ids the current SEARCH returns — the canvas highlight set. Empty in
   *  browse mode (no query, no highlight): the sync runs search→graph, and a
   *  stale set from a superseded search must not outlive its query. */
  const matchedIds = useMemo(
    () => new Set(mode === 'search' && searchRows !== null ? searchRows.map((r) => r.id) : []),
    [mode, searchRows],
  );

  /** Selecting from a ROW — browse feed or search hit, the same shape — also
   *  seeds the graph: the selection is the graph's first node, so the canvas
   *  is live the moment a row opens. A browse row carries `updated_at` into
   *  the seed node (the recency encoding's first source); a search hit does
   *  not (its projection replaced the column with the rank) — the node is
   *  simply neutral until record_get supplies one. */
  function selectFromRow(row: RowSelection) {
    setGraph((g) => (g.nodes.has(row.id)
      ? g
      : {
        ...g,
        nodes: new Map(g.nodes).set(row.id, {
          id: row.id,
          type: row.type,
          title: row.title,
          scope: row.scope,
          privacy: row.privacy,
          updatedAt: row.updatedAt,
        }),
      }));
    setSelectedId(row.id);
  }

  /** The terms box is the panel's switch. A whitespace-only query counts as
   *  empty — it would only ever be a garbage FTS term. */
  function onTermsChange(next: string) {
    setQuery((q) => ({ ...q, terms: next }));
  }

  /** The type chip does double duty: a browse FILTER (Task 4, exclusive
   *  select via `list(type)`) and, in search mode, a QUERY FACET composed
   *  into the record_search body. In search mode it toggles — clicking the
   *  active chip clears the facet, one active value per axis. */
  function onTypeChipClick(type: string) {
    if (mode === 'search') {
      setQuery((q) => ({ ...q, type: q.type === type ? null : type }));
      return;
    }
    setQuery((q) => ({ ...q, type }));
  }

  /** The privacy axis is search-only (list has no privacy param) and toggles
   *  the same way: one active privacy, clicking it again clears it. */
  function onPrivacyChipClick(privacy: string) {
    setQuery((q) => ({ ...q, privacy: q.privacy === privacy ? null : privacy }));
  }

  /** The scope axis composes into list, search AND the counts in both modes;
   *  toggle semantics like the other search facets. */
  function onScopeChipClick(scope: string) {
    setQuery((q) => ({ ...q, scope: q.scope === scope ? null : scope }));
  }

  /** Browse's All: the type axis back to unset (Task 4's affordance). The
   *  scope axis is a chip of its own in BOTH modes and survives — All means
   *  "every type", within whatever scope is selected. */
  function clearTypeFacet() {
    setQuery((q) => ({ ...q, type: null }));
  }

  /**
   * Load more: pass the response cursor back and APPEND. The ref is the
   * synchronous guard (two clicks inside one React batch must not mint two
   * GETs for the same cursor — that is the double-append); the epoch drops
   * a response whose query moved on underneath it (page-one effects bump
   * the epoch). A refused page is "no more rows" — absence, never an error;
   * any other fault surfaces as an Alert beside the STILL-RENDERED rows
   * (a pagination fault must not cost the panel its first page).
   */
  async function loadMore() {
    if (moreInFlightRef.current) return;
    const cursor = mode === 'search' ? searchCursor : browseCursor;
    if (cursor === null) return;
    moreInFlightRef.current = true;
    const epoch = pageEpochRef.current;
    setMoreLoading(true);
    setMoreFault(null);
    try {
      const vault = await readSdk.vault.byId(vaultId);
      if (mode === 'search') {
        const terms = debouncedTerms.trim();
        const resp = await vault.memory.search(terms, {
          limit: LIST_LIMIT,
          cursor,
          ...(query.type !== null ? { type: query.type } : {}),
          ...(query.privacy !== null ? { privacy: query.privacy } : {}),
          ...(query.scope !== null ? { scope: query.scope } : {}),
        });
        if (pageEpochRef.current !== epoch) return;
        setSearchRows((current) => appendPage(current ?? [], searchRowsOf(resp)));
        setSearchCursor(resp.cursor ?? null);
      } else {
        const resp = await vault.memory.list(query.type ?? undefined, {
          limit: LIST_LIMIT,
          cursor,
          ...(query.scope !== null ? { scope: query.scope } : {}),
        });
        if (pageEpochRef.current !== epoch) return;
        setRecords((current) => appendPage(current ?? [], recordsOf(resp)));
        setBrowseCursor(resp.cursor ?? null);
      }
    } catch (e) {
      if (pageEpochRef.current === epoch) {
        if (isRefusal(e)) {
          // Absence: stop offering pages, keep the rows already disclosed.
          if (mode === 'search') setSearchCursor(null);
          else setBrowseCursor(null);
        } else {
          setMoreFault(toDisplayError(e));
        }
      }
    } finally {
      moreInFlightRef.current = false;
      setMoreLoading(false);
    }
  }

  /**
   * One node's neighbourhood, merged in as only NEW nodes/edges — tier 2
   * wires the SDK's edge params (`edgeType`, `direction`) and the budget as
   * `limit`. Unset axes are ABSENT wire keys, never `''`/`'both'` stamped
   * client-side (the omitempty discipline; the server's own default for a
   * missing direction IS both). A refused read (403/404) leaves the canvas
   * untouched and silent — that is the viewer's gate speaking, not a fault.
   * Any other fault is retryable: the node's mark is un-set so its next
   * expand can ask again, and the fault says so beside the content.
   */
  async function expandWith(id: string, params: ExpansionRecord) {
    // Past the cap the graph stops growing — the notice explains, and no
    // further reads are spent on a canvas that will not draw them.
    if (graph.capped || graph.nodes.size >= graphNodeCap) {
      if (!graph.capped) setGraph((g) => ({ ...g, capped: true }));
      return;
    }
    setGraphFault(null);
    setGraph((g) => ({ ...g, expanded: new Map(g.expanded).set(id, params) }));
    let rows: unknown[][];
    try {
      const vault = await readSdk.vault.byId(vaultId);
      rows = (await vault.memory.neighbours(id, {
        limit: params.limit,
        ...(params.edgeType !== null ? { edgeType: params.edgeType } : {}),
        ...(params.direction !== 'both' ? { direction: params.direction } : {}),
      })).rows;
    } catch (e) {
      if (isRefusal(e)) return;
      setGraph((g) => {
        const expanded = new Map(g.expanded);
        expanded.delete(id);
        return { ...g, expanded };
      });
      setGraphFault(toDisplayError(e));
      return;
    }
    const found = rows.map(neighbourOf);
    setGraph((g) => {
      const nodes = new Map(g.nodes);
      const edges = new Map(g.edges);
      for (const { far, edgeType } of found) {
        // '' would render an anonymous node for a null id cell; the near id
        // itself is not its own neighbour (self-loops would double the rank).
        if (far.id === '' || far.id === id) continue;
        if (!nodes.has(far.id)) nodes.set(far.id, far);
        const key = `${id}->${far.id}`;
        if (!edges.has(key)) edges.set(key, { source: id, target: far.id, label: edgeType });
      }
      return { ...g, nodes, edges, capped: g.capped || nodes.size >= graphNodeCap };
    });
  }

  /** The PLAIN expansion — ⌘E and double-click: the unfiltered neighbourhood
   *  under the standing budget. Implicit, so it never re-asks a node the
   *  graph already expanded (the mark the params map carries). */
  function expand(id: string) {
    if (graph.expanded.has(id)) return;
    void expandWith(id, { edgeType: null, direction: 'both', limit: budget });
  }

  /** Advanced Expansion (the node's ⋯ dialog): an EXPLICIT ask, so it runs
   *  even on an already-expanded node — the filters make it a different
   *  query — and its budget becomes the standing client cap. */
  function expandAdvanced(target: { id: string; title: string | null }, a: AdvancedExpansion) {
    setBudget(a.budget);
    void expandWith(target.id, { edgeType: a.edgeType, direction: a.direction, limit: a.budget });
  }

  /**
   * A hub chip's next batch: unlock one budget-sized slice of the hub's
   * drawn degree AND re-read the SAME node with the SAME edge filter but a
   * HIGHER limit (the stored limit + one budget) — the reveal needs rows to
   * draw, not just allowance. `record_neighbours` orders by far id, so the
   * bigger ask is a deterministic superset of the page already drawn: the
   * batch reveals only rows the smaller ask had left out, and the grouping
   * pass re-cuts whatever surplus remains.
   */
  function expandHubBatch(hubId: string) {
    if (hubBatchInFlightRef.current.has(hubId)) return;
    hubBatchInFlightRef.current.add(hubId);
    setGraph((g) => ({
      ...g,
      revealed: new Map(g.revealed).set(hubId, (g.revealed.get(hubId) ?? 0) + budget),
    }));
    const prior = graph.expanded.get(hubId);
    const params: ExpansionRecord = {
      edgeType: prior?.edgeType ?? null,
      direction: prior?.direction ?? 'both',
      limit: (prior?.limit ?? 0) + budget,
    };
    void expandWith(hubId, params).finally(() => {
      hubBatchInFlightRef.current.delete(hubId);
    });
  }

  /** Focus mode (Kumu's pattern): dims the non-neighbours of the current
   *  selection — a canvas VISUAL only, no server calls. Meaningful only with
   *  a selection; without one the toggle is a no-op rather than dimming
   *  everything. */
  function toggleFocusMode() {
    if (selectedId === null) return;
    setFocusMode((f) => !f);
  }

  /** The palette's jump: select the record (seeding the graph from its row
   *  when it is not loaded as a node yet) — the scroll-into-view effect on
   *  `selectedId` brings the pane up. */
  function jumpToRecord(id: string) {
    if (graph.nodes.has(id)) {
      setSelectedId(id);
      return;
    }
    const browseRow = (records ?? []).find((r) => r.id === id);
    if (browseRow !== undefined) {
      selectFromRow(browseRow);
      return;
    }
    const hitRow = (searchRows ?? []).find((r) => r.id === id);
    if (hitRow !== undefined) {
      selectFromRow({ ...hitRow, updatedAt: null });
      return;
    }
    setSelectedId(id);
  }

  // A chip node's click is the BATCH gesture, not a selection — the chip is
  // an affordance, not a record. Everything else selects (single click) /
  // expands (double click) exactly as tier 1 had it.
  const handleNodeClick: NodeMouseHandler = (_e, node) => {
    if (node.id.startsWith(HUB_NODE_PREFIX)) {
      expandHubBatch(node.id.slice(HUB_NODE_PREFIX.length));
      return;
    }
    setSelectedId(node.id);
  };
  const handleNodeDoubleClick: NodeMouseHandler = (_e, node) => {
    if (node.id.startsWith(HUB_NODE_PREFIX)) return;
    expand(node.id);
  };

  // ── Tier 2 derived visuals ─────────────────────────────────────────────
  //
  // Hub grouping (Bloom's node-limit pattern): the pass caps each node's
  // DRAWN degree at HUB_DEGREE_THRESHOLD and collapses the surplus behind
  // one `+N more` chip per hub — never a starburst. The surplus count is
  // LOADED-VISIBLE edges only: relations the viewer's own reads returned
  // and the graph actually holds. It is NOT a server degree and never a
  // claim about relations the gate hides — there is no such read, and the
  // hub guardrail forbids the guess.
  const grouping = useMemo(
    () => groupHubs(graph.nodes.values(), graph.edges.values(), HUB_DEGREE_THRESHOLD, graph.revealed),
    [graph.nodes, graph.edges, graph.revealed],
  );

  // Focus mode's keep-set: the selection plus its direct neighbours (either
  // direction) among loaded nodes. Null when focus is off or nothing is
  // selected — then the recency encoding owns the opacity axis instead.
  const focusIds = useMemo(() => {
    if (!focusMode || selectedId === null) return null;
    const ids = new Set<string>([selectedId]);
    for (const e of graph.edges.values()) {
      if (e.source === selectedId) ids.add(e.target);
      if (e.target === selectedId) ids.add(e.source);
    }
    return ids;
  }, [focusMode, selectedId, graph.edges]);

  // The recency encoding: opacity by quartile among loaded nodes that HAVE
  // an `updated_at` (feed rows, plus record_get once a node is opened).
  // Nodes without one are absent from the map → neutral opacity.
  const recencyOpacity = useMemo(
    () => recencyOpacityById([...graph.nodes.values()]),
    [graph.nodes],
  );

  /** The one opacity a node renders with. Focus mode wins over recency while
   *  it is on (dimming is the stronger signal); otherwise the recency
   *  quartile owns the axis, and `undefined` means NO encoding — the style
   *  key stays absent, not pinned to 1. */
  const nodeOpacity = useCallback((id: string): number | undefined => {
    if (focusIds !== null) return focusIds.has(id) ? 1 : FOCUS_DIM_OPACITY;
    return recencyOpacity.get(id);
  }, [focusIds, recencyOpacity]);

  // The evidence strip's data: loaded records whose edges POINT AT the
  // selection, in discovery order. Client-side by necessity AND honesty —
  // there is no reverse-edge server query wired for this, so the strip is
  // labelled "in current view" and never claims the whole bank.
  const citers = useMemo<MemoryCiter[]>(() => {
    if (selectedId === null) return [];
    const out: MemoryCiter[] = [];
    for (const e of graph.edges.values()) {
      if (e.target !== selectedId) continue;
      const n = graph.nodes.get(e.source);
      if (n === undefined) continue;
      out.push({ id: n.id, title: n.title, edgeType: e.label });
    }
    return out;
  }, [selectedId, graph.edges, graph.nodes]);

  // The row citation affordance's counts: how many loaded edges point at
  // each row (zero-count rows render nothing — no empty speculation).
  const citerCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of graph.edges.values()) {
      counts.set(e.target, (counts.get(e.target) ?? 0) + 1);
    }
    return counts;
  }, [graph.edges]);

  // The palette's record corpus: everything currently loaded — panel rows
  // seed the set (insertion order: the feed's newest-first), and graph nodes
  // OVERRIDE their entries (a node's title is the graph's own view of it).
  const paletteRecords = useMemo<MemoryPaletteRecord[]>(() => {
    const byId = new Map<string, MemoryPaletteRecord>();
    for (const r of records ?? []) byId.set(r.id, { id: r.id, title: r.title, type: r.type });
    for (const r of searchRows ?? []) {
      if (!byId.has(r.id)) byId.set(r.id, { id: r.id, title: r.title, type: r.type });
    }
    for (const n of graph.nodes.values()) {
      byId.set(n.id, { id: n.id, title: n.title ?? n.id, type: n.type });
    }
    return [...byId.values()];
  }, [records, searchRows, graph.nodes]);

  // Relation types OBSERVED in the loaded neighbours rows — the Advanced
  // Expansion dialog's edge-type vocabulary. Client-side observation of what
  // the viewer's reads disclosed; never a server enumeration claim.
  const observedEdgeTypes = useMemo(() => {
    const types = new Set<string>();
    for (const e of graph.edges.values()) {
      if (e.label !== null) types.add(e.label);
    }
    return [...types].sort();
  }, [graph.edges]);

  const chipEdges = useMemo(
    () => grouping.hubs.map((h) => ({
      source: h.hubId,
      target: `${HUB_NODE_PREFIX}${h.hubId}`,
      label: null,
    })),
    [grouping],
  );
  // The DRAWN edge list: the kept edges plus one edge per hub chip. The
  // readout's relation count reports THIS (what the canvas shows); the
  // loaded surplus rides the chips, which name it themselves.
  const drawnEdges = useMemo(() => [...grouping.keptEdges, ...chipEdges], [grouping, chipEdges]);

  const flowEdges = useMemo(
    () => drawnEdges.map((e, i) => memoryEdge(
      e.source,
      e.target,
      e.label,
      i,
      // Focus mode dims edges too: everything not incident to the selection.
      focusIds !== null && selectedId !== null && e.source !== selectedId && e.target !== selectedId,
    )),
    [drawnEdges, focusIds, selectedId],
  );
  // The canvas's half of the shared query: a node the current SEARCH returns
  // carries the match ring (blue) on top of its privacy tint; the selected
  // node keeps the violet ring. Browse mode passes no match set — the tint
  // contract is untouched either way.
  const flowNodes = useMemo(
    () => layout(
      [
        ...grouping.visible.map((r) => memoryNode(r, r.id === selectedId, matchedIds.has(r.id), {
          opacity: nodeOpacity(r.id),
          // The node's context menu: opens the Advanced Expansion dialog.
          // stopPropagation inside keeps it from selecting the node.
          onMenu: () => setExpandDialogFor({ id: r.id, title: r.title }),
        })),
        ...grouping.hubs.map((h) => hubChipNode(h.hubId, h.moreCount, nodeOpacity(h.hubId))),
      ],
      flowEdges,
    ),
    // nodeOpacity closes over focusIds/recencyOpacity; both are deps.
    [grouping, selectedId, matchedIds, flowEdges, nodeOpacity],
  );

  // Tier 2 keyboard discipline, one window listener: ⌘K toggles the palette
  // (⌃K too — same gesture on the other platform), ⌘E expands the SELECTED
  // node, plain `f` toggles focus mode. Single-letter keys never fire while
  // the user is typing (inputs, textareas, contenteditable, or anywhere
  // inside a dialog — the palette's own input included); ⌘-combos stay
  // global because they are never text.
  //
  // The listener is registered ONCE and reads through a ref of the latest
  // handlers — re-subscribing on every graph mutation would be churn, and a
  // stale closure would expand the wrong node.
  const shortcutsRef = useRef<{ selectedId: string | null; expand: (id: string) => void; toggleFocusMode: () => void }>({
    selectedId: null,
    expand: () => {},
    toggleFocusMode: () => {},
  });
  useEffect(() => {
    shortcutsRef.current = { selectedId, expand, toggleFocusMode };
  });
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      if (mod && key === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      const { selectedId: selection, expand: doExpand, toggleFocusMode: doToggleFocus } = shortcutsRef.current;
      if (mod && key === 'e') {
        if (selection !== null) {
          e.preventDefault();
          doExpand(selection);
        }
        return;
      }
      if (mod || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t !== null && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      // `closest` guards the window target itself (a bare-window keydown
      // has no Element methods) and any other non-element target.
      if (t !== null && typeof t.closest === 'function' && t.closest('[role="dialog"]') !== null) return;
      if (e.key === 'f') {
        e.preventDefault();
        doToggleFocus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Selecting a record scrolls the explore half into view — row clicks,
  // palette jumps and evidence-strip citations all land here, so the pane is
  // never silently off-screen after a jump. `block: 'nearest'` keeps an
  // already-visible pane still.
  const exploreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectedId === null) return;
    const el = exploreRef.current;
    if (el !== null && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedId]);

  // The palette's query actions: restore writes the saved query in ONE set
  // (the debounce effect settles the terms twin), clear resets every axis.
  // Focus rides `toggleFocusMode` — the same guarded toggle as the button.
  const restoreView = useCallback((q: MemoryQuerySnapshot) => {
    setQuery({ terms: q.terms, type: q.type, privacy: q.privacy, scope: q.scope });
  }, []);
  const clearFilters = useCallback(() => {
    setQuery(EMPTY_QUERY);
  }, []);

  // The pane shows the full row once it lands; until then (or for good, on a
  // refused get) it shows the projection fields already disclosed. The
  // fallback's `updatedAt` rides when the source carried one (graph node
  // seeded from a feed row, or merged from record_get) so the recency chip
  // renders even before the full row lands.
  const paneRecord: MemoryRecordDetail | null = (() => {
    if (selectedId === null) return null;
    if (detail !== null && detail.id === selectedId) return detail;
    const node = graph.nodes.get(selectedId);
    if (node !== undefined) {
      return {
        id: node.id, type: node.type, title: node.title, body: null,
        scope: node.scope, privacy: node.privacy, createdAt: null, updatedAt: node.updatedAt ?? null,
      };
    }
    const browseRow = (records ?? []).find((r) => r.id === selectedId);
    const hitRow = browseRow === undefined
      ? (searchRows ?? []).find((r) => r.id === selectedId)
      : undefined;
    if (browseRow !== undefined) {
      return {
        id: browseRow.id, type: browseRow.type, title: browseRow.title, body: null,
        scope: browseRow.scope, privacy: browseRow.privacy, createdAt: null, updatedAt: browseRow.updatedAt,
      };
    }
    if (hitRow !== undefined) {
      // A search hit's projection carried the rank where the date would be:
      // no `updatedAt` until record_get supplies one.
      return {
        id: hitRow.id, type: hitRow.type, title: hitRow.title, body: null,
        scope: hitRow.scope, privacy: hitRow.privacy, createdAt: null, updatedAt: null,
      };
    }
    return null;
  })();

  // The panel's rows in ONE shape: browse renders the feed projection, search
  // the hit projection — same list markup, same click, only the meta line
  // differs (a date for browse, the raw rank for search) and search rows may
  // carry a snippet.
  const panelRows: RowView[] = mode === 'search'
    ? (searchRows ?? []).map(searchRowView)
    : (records ?? []).map(browseRowView);
  // Search is pending not only while its GET is in flight but also while the
  // query is still settling in the debounce — rendering the zero line for
  // results that were never asked for would be a lie.
  const panelLoading = mode === 'search'
    ? searchLoading || debouncedTerms.trim() === ''
    : recordsLoading;
  const panelFault = mode === 'search' ? searchFault : recordsFault;
  // The Load-more control: offered only while a page of rows is on screen
  // AND the response carried a cursor (absent = last page → hidden).
  const panelCursor = mode === 'search' ? searchCursor : browseCursor;
  const showLoadMore = !panelLoading && panelFault === null && panelRows.length > 0 && panelCursor !== null;

  return (
    <div className="vault-orgs__memory">
      <div className="vault-orgs__members-head">
        <h3 className="vault-orgs__subhead">Memory bank</h3>
        {/* The ⌘K palette's visible affordance — records, saved views and
            actions in one keyboard-first place. */}
        <button
          type="button"
          className="vault-orgs__memory-palette-open"
          data-testid="memory-palette-open"
          aria-label="Open the command palette"
          onClick={() => setPaletteOpen(true)}
        >
          ⌘K
        </button>
        {/* The panel's switch: a query here is search mode, clearing it
            returns the browse feed. Whitespace-only counts as empty. */}
        <TextField
          className="vault-orgs__memory-search"
          size="small"
          type="search"
          label="Search the bank"
          placeholder="Full-text match"
          value={query.terms}
          onChange={(e) => onTermsChange(e.target.value)}
        />
      </div>
      <p className="vault-orgs__memory-sub">
        What this organization’s runs and documents have taught it — newest first,
        already filtered to what your grants let you see.
      </p>

      {(countsFault !== null || recordsFault !== null || searchFault !== null || scopesFault !== null || moreFault !== null) && (
        <Alert severity="error" className="vault-orgs__memory-fault">
          The memory bank could not be read. {[countsFault, recordsFault, searchFault, scopesFault, moreFault].filter(Boolean).join(' ')}
        </Alert>
      )}
      {(detailFault !== null || graphFault !== null) && (
        <Alert severity="error" className="vault-orgs__memory-fault">
          The record detail or graph could not be read. {[detailFault, graphFault].filter(Boolean).join(' ')}
        </Alert>
      )}

      {/* The readout sits OUTSIDE the mode gate on purpose: it counts the
          viewer's admitted bank (the chips' sum, narrowed by the scope axis
          alone), so neither a narrowed feed nor a faceted query may shrink
          it — type/privacy/terms narrow the query, never the gate. */}
      {!countsLoading && countsFault === null && (
        <p className="vault-orgs__memory-gate" data-testid="memory-gate-readout">
          {`Your gate · ${gateTotal} records`}
        </p>
      )}

      {/* The scope axis: BOTH modes (list and search both take scope), the
          options exactly what the gated scopes.list returned — the
          viewer-visible scopes, no more. A refused/empty read renders no
          axis at all rather than inventing one. */}
      {scopeOptions !== null && scopeOptions.length > 0 && (
        <div className="vault-orgs__memory-chips" role="group" aria-label="Scopes">
          {scopeOptions.map((s) => (
            <button
              key={s.name}
              type="button"
              className={`vault-orgs__memory-chip${query.scope === s.name ? ' vault-orgs__memory-chip--active' : ''}`}
              aria-pressed={query.scope === s.name}
              onClick={() => onScopeChipClick(s.name)}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {mode === 'browse' && (
        <>
          {countsLoading && (
            <div className="vault-orgs__detail-loading"><CircularProgress size={20} /></div>
          )}
          {!countsLoading && countsFault === null && (
            <div className="vault-orgs__memory-chips" role="group" aria-label="Memory types">
              <button
                type="button"
                className={`vault-orgs__memory-chip${query.type === null ? ' vault-orgs__memory-chip--active' : ''}`}
                aria-pressed={query.type === null}
                onClick={clearTypeFacet}
              >
                All
              </button>
              {(counts ?? []).map((c) => (
                <button
                  key={c.type}
                  type="button"
                  className={`vault-orgs__memory-chip${query.type === c.type ? ' vault-orgs__memory-chip--active' : ''}`}
                  aria-pressed={query.type === c.type}
                  onClick={() => onTypeChipClick(c.type)}
                >
                  {c.type} · {c.count}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {mode === 'search' && (
        <>
          {/* Search mode: the SAME type chips, now query facets — no `All`
              needed when toggling the active chip clears the axis. */}
          {!countsLoading && countsFault === null && (
            <div className="vault-orgs__memory-chips" role="group" aria-label="Memory types">
              {(counts ?? []).map((c) => (
                <button
                  key={c.type}
                  type="button"
                  className={`vault-orgs__memory-chip${query.type === c.type ? ' vault-orgs__memory-chip--active' : ''}`}
                  aria-pressed={query.type === c.type}
                  onClick={() => onTypeChipClick(c.type)}
                >
                  {c.type} · {c.count}
                </button>
              ))}
            </div>
          )}
          {/* The privacy axis, search-only (list takes no privacy param):
              narrowing within the viewer's gate, so a member filtering
              `private` gets zero rows, not a leak — the server ANDs the
              facet with the ceiling. */}
          <div className="vault-orgs__memory-chips" role="group" aria-label="Privacy">
            {PRIVACY_FACETS.map((p) => (
              <button
                key={p}
                type="button"
                className={`vault-orgs__memory-chip${query.privacy === p ? ' vault-orgs__memory-chip--active' : ''}`}
                aria-pressed={query.privacy === p}
                onClick={() => onPrivacyChipClick(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </>
      )}

      {panelFault === null && (panelLoading
        ? (
          <div className="vault-orgs__detail-loading"><CircularProgress size={20} /></div>
        )
        : (panelRows.length === 0
          ? (
            /* ONE empty state for both panels and both causes: "nothing
               matches" and "everything that matches is outside your view"
               are indistinguishable by design, so the same component renders
               the same line for each — with the access explainer folded in. */
            <MemoryEmptyState access={access} />
          )
          : (
            <>
              <ul className="vault-orgs__memory-rows">
                {panelRows.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      className="vault-orgs__memory-row"
                      data-testid="memory-row"
                      aria-current={selectedId === r.id ? 'true' : undefined}
                      onClick={() => selectFromRow(r)}
                    >
                      <span className="vault-orgs__memory-type">{r.type}</span>
                      <span className="vault-orgs__memory-title">{r.title}</span>
                      <span className="vault-orgs__memory-meta">{r.meta}</span>
                      {/* The citation affordance (tier 2): loaded records
                          whose edges point at this row. Counted from the
                          graph the viewer built, so it appears only when the
                          current view actually cites the record — clicking
                          the row opens the pane where the strip lives. */}
                      {(citerCounts.get(r.id) ?? 0) > 0 && (
                        <span className="vault-orgs__memory-row-cited" data-testid="memory-row-cited">
                          {`cited by ${citerCounts.get(r.id)}`}
                        </span>
                      )}
                      {/* The search snippet: FTS markers → <mark> spans,
                          everything else escaped by React. Absent on browse
                          rows and on older servers (rendered as no line at
                          all, silently). */}
                      {r.snippet !== null && (
                        <span className="vault-orgs__memory-snippet" data-testid="memory-snippet">
                          {r.snippet}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {showLoadMore && (
                <button
                  type="button"
                  className="vault-orgs__memory-more"
                  data-testid="memory-load-more"
                  disabled={moreLoading}
                  onClick={() => { void loadMore(); }}
                >
                  {moreLoading ? 'Loading…' : 'Load more'}
                </button>
              )}
            </>
          )))}

      {/* The explore half, outside the mode gate: whatever panel a record
          comes from (browse feed or search hit), it opens HERE. */}
      {selectedId !== null && (
        <div className="vault-orgs__memory-explore" ref={exploreRef}>
          {/* The pane's evidence strip (tier 2): citers from the LOADED
              graph — the honest "in current view" line, absent when nothing
              loaded cites the record. */}
          <MemoryDetailPane
            record={paneRecord}
            loading={detailLoading}
            citers={citers}
            onCite={(id) => setSelectedId(id)}
          />
          {graph.nodes.size > 0 && (
            <div className="vault-orgs__memory-graph">
              <div className="vault-orgs__memory-graph-head">
                {/* Records = everything LOADED (the chips name their own
                    surplus); relations = what is DRAWN (kept edges + one per
                    hub chip). */}
                <span className="vault-orgs__memory-graph-count">
                  {`${graph.nodes.size} ${graph.nodes.size === 1 ? 'record' : 'records'} · ${drawnEdges.length} ${drawnEdges.length === 1 ? 'relation' : 'relations'}`}
                </span>
                <MemoryPrivacyLegend />
                {/* Focus mode (tier 2): dims the non-neighbours of the
                    selection — a canvas visual only, never a server call. */}
                <button
                  type="button"
                  className={`vault-orgs__memory-focus${focusMode ? ' vault-orgs__memory-focus--on' : ''}`}
                  data-testid="memory-focus-toggle"
                  aria-pressed={focusMode}
                  onClick={toggleFocusMode}
                >
                  {focusMode ? 'Focus on' : 'Focus'}
                </button>
              </div>
              {graph.capped && (
                <p className="vault-orgs__memory-cap" data-testid="memory-cap-notice">
                  {`Stopped expanding at ${graphNodeCap} records — refine your query to see more.`}
                </p>
              )}
              <p className="vault-orgs__memory-graph-hint">
                Click a node to open it · double-click or ⌘E to add its neighbourhood · ⋯ for a filtered expansion.
                {matchedIds.size > 0 && ' Ringed nodes match your search.'}
              </p>
              {/* The recency encoding's legend line: opacity is RELATIVE to
                  the loaded records (quartiles), never absolute dates on
                  nodes. Rendered only when at least one loaded node carries
                  an `updated_at` — a legend for an invisible encoding would
                  be noise. */}
              {recencyOpacity.size > 0 && (
                <p className="vault-orgs__memory-recency-legend" data-testid="memory-recency-legend">
                  Node opacity · newer updates brighter, relative to the loaded records
                </p>
              )}
              <MemoryGraphCanvas
                nodes={flowNodes}
                edges={flowEdges}
                onNodeClick={handleNodeClick}
                onNodeDoubleClick={handleNodeDoubleClick}
              />
            </div>
          )}
        </div>
      )}

      {/* Advanced Expansion (tier 2): the node's ⋯ dialog — edge type
          (observed vocabulary only), direction, node budget. */}
      <MemoryExpandDialog
        open={expandDialogFor !== null}
        node={expandDialogFor}
        edgeTypes={observedEdgeTypes}
        defaultBudget={budget}
        onExpand={(a) => { if (expandDialogFor !== null) expandAdvanced(expandDialogFor, a); }}
        onClose={() => setExpandDialogFor(null)}
      />

      {/* The ⌘K palette (tier 2): records, personal saved views, actions. */}
      <MemoryCommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        vaultId={vaultId}
        records={paletteRecords}
        currentQuery={query}
        focusOn={focusMode}
        canFocus={selectedId !== null}
        onToggleFocus={toggleFocusMode}
        onClearFilters={clearFilters}
        onRestoreView={restoreView}
        onJumpToRecord={jumpToRecord}
      />
    </div>
  );
}
